import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AppShell } from './components/AppShell'
import { Hello } from './pages/Hello'
import { Index, MissionControlClassic } from './pages/Index'
import { IndexCA } from './pages/IndexCA'
import { IndexWarmies } from './pages/IndexWarmies'

// Lazy-loaded pages: each becomes its own chunk loaded on first navigation.
// Pages export named components (not default), so we reshape with .then().
const CreativeStudioV2       = lazy(() => import(/* webpackChunkName: "creative-studio-v2" */ './pages/CreativeStudioV2').then(m => ({ default: m.CreativeStudioV2 })))
const CreativeStrategy       = lazy(() => import(/* webpackChunkName: "creative-strategy" */ './pages/CreativeStrategy').then(m => ({ default: m.CreativeStrategy })))
// Creative suite (PR #253): Coverage + Levers. Old creative pages stay until the
// Horizon gating (#209) lands; then routes consolidate (proposal 4.8).
const Coverage               = lazy(() => import(/* webpackChunkName: "coverage" */ './pages/Coverage').then(m => ({ default: m.Coverage })))
const Levers                 = lazy(() => import(/* webpackChunkName: "levers" */ './pages/Levers').then(m => ({ default: m.Levers })))
const SPSandbox              = lazy(() => import(/* webpackChunkName: "sp-sandbox" */ './pages/SPSandbox').then(m => ({ default: m.SPSandbox })))
const PersonaSetup           = lazy(() => import(/* webpackChunkName: "persona-setup" */ './components/PersonaSetup').then(m => ({ default: m.PersonaSetup })))
const Settings               = lazy(() => import(/* webpackChunkName: "settings" */ './pages/Settings').then(m => ({ default: m.Settings })))
const Ghost                  = lazy(() => import(/* webpackChunkName: "ghost" */ './pages/Ghost').then(m => ({ default: m.Ghost })))
const Horizon                = lazy(() => import(/* webpackChunkName: "horizon" */ './pages/Horizon').then(m => ({ default: m.Horizon })))
const Book                   = lazy(() => import(/* webpackChunkName: "book" */ './pages/Book').then(m => ({ default: m.Book })))
const MMM                    = lazy(() => import(/* webpackChunkName: "mmm" */ './pages/MMM').then(m => ({ default: m.MMM })))
const Reports                = lazy(() => import(/* webpackChunkName: "reports" */ './pages/Reports').then(m => ({ default: m.Reports })))
// AI Insights agentic chat (rebuild) — sidebar "Insights" points here; the
// classic one-shot report builder stays reachable at /reports until parity.
const InsightsChat           = lazy(() => import(/* webpackChunkName: "insights-chat" */ './pages/InsightsChat').then(m => ({ default: m.InsightsChat })))
const Playbook               = lazy(() => import(/* webpackChunkName: "playbook" */ './pages/Playbook').then(m => ({ default: m.Playbook })))
const Creative               = lazy(() => import(/* webpackChunkName: "creative" */ './pages/Creative').then(m => ({ default: m.Creative })))
const Financials             = lazy(() => import(/* webpackChunkName: "financials" */ './pages/Financials').then(m => ({ default: m.Financials })))
const FinancialsCA           = lazy(() => import(/* webpackChunkName: "financials-ca" */ './pages/FinancialsCA').then(m => ({ default: m.FinancialsCA })))
const Pnl4Patriots           = lazy(() => import(/* webpackChunkName: "pnl-4patriots" */ './pages/Pnl4Patriots').then(m => ({ default: m.Pnl4Patriots })))
const Attribution            = lazy(() => import(/* webpackChunkName: "attribution" */ './pages/Attribution').then(m => ({ default: m.Attribution })))
const AttributionCA          = lazy(() => import(/* webpackChunkName: "attribution-ca" */ './pages/AttributionCA').then(m => ({ default: m.AttributionCA })))
const MmmCA                  = lazy(() => import(/* webpackChunkName: "mmm-ca" */ './pages/MmmCA').then(m => ({ default: m.MmmCA })))
const Experiments            = lazy(() => import(/* webpackChunkName: "experiments" */ './pages/Experiments').then(m => ({ default: m.Experiments })))
const ExperimentDetail       = lazy(() => import(/* webpackChunkName: "experiment-detail" */ './pages/ExperimentDetail').then(m => ({ default: m.ExperimentDetail })))
const ExperimentGuide        = lazy(() => import(/* webpackChunkName: "experiment-guide" */ './pages/ExperimentGuide').then(m => ({ default: m.ExperimentGuide })))
const Playbook4Patriots      = lazy(() => import(/* webpackChunkName: "playbook-4patriots" */ './pages/Playbook4Patriots').then(m => ({ default: m.Playbook4Patriots })))
const PlaybookCA             = lazy(() => import(/* webpackChunkName: "playbook-ca" */ './pages/PlaybookCA').then(m => ({ default: m.PlaybookCA })))
const PlaybookWarmies        = lazy(() => import(/* webpackChunkName: "playbook-warmies" */ './pages/PlaybookWarmies').then(m => ({ default: m.PlaybookWarmies })))
const GhostCA                = lazy(() => import(/* webpackChunkName: "ghost-ca" */ './pages/GhostCA').then(m => ({ default: m.GhostCA })))
const CreativeIncrementality = lazy(() => import(/* webpackChunkName: "creative-incrementality" */ './pages/CreativeIncrementality').then(m => ({ default: m.CreativeIncrementality })))
// Intelligence v2 deprecated — the best pieces (Hero Band, Strategic Map,
// Risers & Fallers, interactive tables, bucket drawer) were merged into
// /creative-intelligence. Old URLs redirect so external bookmarks resolve.
const Agents                 = lazy(() => import(/* webpackChunkName: "agents" */ './pages/Agents').then(m => ({ default: m.Agents })))
const ChannelGoogle          = lazy(() => import(/* webpackChunkName: "channel-google" */ './pages/ChannelGoogle').then(m => ({ default: m.ChannelGoogle })))
const ChannelMeta            = lazy(() => import(/* webpackChunkName: "channel-meta" */ './pages/ChannelMeta').then(m => ({ default: m.ChannelMeta })))
// CA plan sheet (docs/PLAN_SYNC.md): its own tab; the landing view is unchanged.
const Plan                   = lazy(() => import(/* webpackChunkName: "plan" */ './pages/Plan').then(m => ({ default: m.Plan })))
const Admin                  = lazy(() => import(/* webpackChunkName: "admin" */ './pages/Admin').then(m => ({ default: m.Admin })))


// Routes mirror the vanilla URLs (/creative-studio.html etc.) so the React
// app can sit at app.9fs.ai/* alongside Flask during the migration. Every
// vanilla page in app/*.html has a corresponding React route below.
export default function App() {
  return (
    <AppShell>
      <Suspense fallback={
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 40, color: 'var(--text-muted)', fontSize: 14,
        }}>
          Loading…
        </div>
      }>
        <Routes>
          {/* Clean URLs are canonical (sidebar uses them). Each page also has a
              .html alias so old bookmarks + cross-page deep links continue to
              work. New code should use the clean variant. */}

          {/* Root / Mission Control */}
          <Route path="/"               element={<Index />} />
          <Route path="/index.html"     element={<Index />} />
          <Route path="/index-ca"       element={<IndexCA />} />
          <Route path="/index-ca.html"  element={<IndexCA />} />
          <Route path="/index-warmies"  element={<IndexWarmies />} />

          {/* Creative */}
          {/* /creative = Performance — buyer's daily triage. Restored as
              first-class after the Hub experiment was superseded by the
              three-audience IA (see Sidebar.tsx for the model). */}
          {/* /creative-strategy restored as its own page (2026-06-10) — Brent's
              standalone Strategy page, no longer a tab inside Intelligence. */}
          <Route path="/creative-strategy"            element={<CreativeStrategy />} />
          <Route path="/coverage"                     element={<Coverage />} />
          <Route path="/levers"                       element={<Levers />} />
          <Route path="/meta-sandbox"                 element={<SPSandbox />} />
          <Route path="/persona-setup"                element={<PersonaSetup />} />
          <Route path="/creative"                     element={<Creative />} />
          <Route path="/creative.html"                element={<Creative />} />
          {/* /creative-studio (the original Studio) is deprecated — redirected to
              Studio (beta) which is /studio-v2. Old URLs continue to work. */}
          <Route path="/creative-studio"              element={<Navigate to="/studio-v2" replace />} />
          <Route path="/creative-studio.html"         element={<Navigate to="/studio-v2" replace />} />
          <Route path="/creative-studio-v2"           element={<Navigate to="/studio-v2" replace />} />
          <Route path="/studio-v2"                    element={<CreativeStudioV2 />} />
          {/* /creative-intelligence = Intelligence — strategist's narrative
              surface (Hero Band, Strategic Map scatter, Personas, Audience,
              Hooks, Briefs). Owns the "what's winning, what do we build next"
              story. */}
          <Route path="/creative-intelligence"        element={<CreativeIncrementality />} />
          <Route path="/creative_incrementality.html" element={<CreativeIncrementality />} />
          {/* Intelligence v2 deprecated — see comment above. */}
          <Route path="/intelligence-v2"               element={<Navigate to="/creative-intelligence" replace />} />
          <Route path="/creative-intelligence-v2"      element={<Navigate to="/creative-intelligence" replace />} />

          {/* Measurement */}
          <Route path="/financials"         element={<Financials />} />
          <Route path="/financials.html"    element={<Financials />} />
          <Route path="/financials-ca"      element={<FinancialsCA />} />
          <Route path="/financials-ca.html" element={<FinancialsCA />} />
          <Route path="/pnl-4patriots"      element={<Pnl4Patriots />} />
          <Route path="/pnl-4patriots.html" element={<Pnl4Patriots />} />
          <Route path="/attribution"        element={<Attribution />} />
          <Route path="/attribution.html"   element={<Attribution />} />
          <Route path="/attribution-ca"     element={<AttributionCA />} />
          <Route path="/attribution-ca.html" element={<AttributionCA />} />
          <Route path="/mmm"                element={<MMM />} />
          <Route path="/mmm.html"           element={<MMM />} />
          <Route path="/mmm-ca"             element={<MmmCA />} />
          <Route path="/mmm-ca.html"        element={<MmmCA />} />
          <Route path="/ghost"              element={<Ghost />} />
          <Route path="/ghost.html"         element={<Ghost />} />
          <Route path="/incrementality"     element={<Ghost />} />
          <Route path="/ghost-ca"           element={<GhostCA />} />
          <Route path="/ghost-ca.html"      element={<GhostCA />} />
          {/* Horizon rolled back as the home page (2026-10-12); kept reachable
              here, off the nav, so it can be switched back on. */}
          <Route path="/horizon"            element={<Horizon />} />
          <Route path="/horizon.html"       element={<Horizon />} />
          {/* Daily Brief retired 2026-09-10: its P0-P3 priority cards were the
              action queue, which Horizon now owns. Redirect, don't 404. */}
          <Route path="/daily-brief"        element={<Navigate to="/" replace />} />
          <Route path="/daily-brief.html"   element={<Navigate to="/" replace />} />
          <Route path="/mission-control-classic" element={<MissionControlClassic />} />
          <Route path="/book"               element={<Book />} />
          <Route path="/book.html"          element={<Book />} />

          {/* Workflow */}
          <Route path="/experiments"             element={<Experiments />} />
          <Route path="/experiments.html"        element={<Experiments />} />
          <Route path="/experiment-detail"       element={<ExperimentDetail />} />
          <Route path="/experiment-detail.html"  element={<ExperimentDetail />} />
          <Route path="/experiment-guide"        element={<ExperimentGuide />} />
          <Route path="/experiment-guide.html"   element={<ExperimentGuide />} />
          <Route path="/playbook"                element={<Playbook />} />
          <Route path="/playbook.html"           element={<Playbook />} />
          <Route path="/playbook-4patriots"      element={<Playbook4Patriots />} />
          <Route path="/playbook-4patriots.html" element={<Playbook4Patriots />} />
          <Route path="/playbook-ca"             element={<PlaybookCA />} />
          <Route path="/playbook-ca.html"        element={<PlaybookCA />} />
          <Route path="/playbook-warmies"        element={<PlaybookWarmies />} />
          <Route path="/playbook-warmies.html"   element={<PlaybookWarmies />} />
          <Route path="/plan"                    element={<Plan />} />

          {/* Channels (deep-dives) */}
          <Route path="/channel-google"      element={<ChannelGoogle />} />
          <Route path="/channel_google.html" element={<ChannelGoogle />} />
          <Route path="/channel-meta"        element={<ChannelMeta />} />
          <Route path="/channel_meta.html"   element={<ChannelMeta />} />

          {/* Tools */}
          <Route path="/insights"       element={<InsightsChat />} />
          <Route path="/reports"        element={<Reports />} />
          <Route path="/reports.html"   element={<Reports />} />
          <Route path="/settings"       element={<Settings />} />
          <Route path="/settings.html"  element={<Settings />} />
          <Route path="/agents"         element={<Agents />} />
          <Route path="/agents.html"    element={<Agents />} />
          <Route path="/admin"          element={<Admin />} />
          <Route path="/admin.html"     element={<Admin />} />

          {/* Validation placeholder + catch-all */}
          <Route path="/hello"                       element={<Hello />} />
          <Route path="*"                            element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AppShell>
  )
}
